import React from "react";
import { getBarColour } from "../../store/helper";
import useTheme from "../../../../common/hooks/useTheme";

type Item = {
  label: string;
  value?: number | string;
  maxValue?: number;
  percentage: string;
  unit?: string;
};

type Props = {
  data: Item[];
  type: string;
};

const BreakdownBarChart = ({ data, type }: Props) => {
  const theme = useTheme();

  const addedValues = [];
  let sum = 0;

  for (let i = 0; i < data.length; i++) {
    sum += +data[i].percentage.slice(0, -1);
    addedValues.push({ index: i, value: sum });
  }

  return (
    <div style={styles.container(theme)}>
      <div style={styles.barContainer(theme)}>
        {addedValues.reverse().map((el) => (
          <div
            key={el.index}
            style={styles.barInner(
              theme,
              `${el.value}%`,
              getBarColour(el.index, data.length, type, theme)
            )}
          />
        ))}
      </div>
      {data.map((el, index) => (
        <div
          key={index}
          style={styles.breakdownDetailContainer(
            theme,
            index === data.length - 1
          )}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={styles.breakdownDetailDot(
                theme,
                getBarColour(index, data.length, type, theme)
              )}
            />
            <div style={styles.breakdownDetailLabel(theme)}>{el.label}</div>
            {(type === "withPercentage" || type === "withPercentageAndTime") &&
              el.percentage !== "NaN%" && (
                <span style={styles.breakdownDetailPercentage(theme)}>
                  {el.percentage}
                </span>
              )}
          </div>
          <div style={{ display: "flex" }}>
            {type === "withDurationAndPercentage" && (
              <span
                style={{
                  ...styles.breakdownDetailValue(theme),
                  marginRight: "8px",
                }}
              >
                {el.value}
              </span>
            )}
            {type === "withPercentage" ? (
              <span style={styles.breakdownDetailValue(theme)}>
                {`${Math.round((el.value as number) || 0)} ${el.unit}`}
              </span>
            ) : type === "withPercentageAndTime" ? (
              <span style={styles.breakdownDetailValue(theme)}>{el.value}</span>
            ) : (
              <span style={styles.breakdownDetailLabel(theme)}>
                {el.percentage !== "NaN%" ? el.percentage : "0%"}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BreakdownBarChart;

const styles = {
  container: (theme: any) => ({
    width: "100%",
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    marginTop: "8px",
    marginBottom: "8px",
  }),
  barContainer: (theme: any) => ({
    flex: 1,
    height: "6px",
    margin: "4px 0",
    backgroundColor: theme.colorTokens.P[10],
    borderRadius: "38px",
    overflow: "hidden",
    position: "relative" as "relative",
  }),
  barInner: (theme: any, customWidth: string, barColor: string) => ({
    width: customWidth,
    height: "6px",
    borderRadius: "38px",
    backgroundColor: barColor,
    position: "absolute" as "absolute",
    top: 0,
    left: 0,
  }),
  breakdownDetailContainer: (theme: any, isLastElement: boolean) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "16px",
    paddingBottom: "8px",
    borderBottom: isLastElement
      ? "none"
      : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  breakdownDetailDot: (theme: any, dotColor: string) => ({
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: dotColor,
    marginRight: "8px",
  }),
  breakdownDetailLabel: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginRight: "8px",
  }),
  breakdownDetailPercentage: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  breakdownDetailValue: (theme: any) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
